// src/components/DelegationComponents.jsx
import React from 'react';
// No additional imports needed as this file only contains presentational components

// Component for user info panel (left sidebar)
export const UserInfoPanel = ({ 
  account, 
  tokenBalance, 
  currentDelegate, 
  governanceInfo, 
  delegatedVotingPower,
  userDelegators,
  votingHistory,
  isUserDelegator,
  delegatorProfile,
  operatorInfo,
  activeProposals,
  loading,
  onEditProfile,
  onToggleAcceptingDelegations,
  onRemoveDelegation,
  formatAddress,
  formatTokens
}) => {
  return (
    <div className="col-span-1">
      <div className="bg-blue-50 p-6 rounded-lg shadow-sm">
        <h2 className="text-xl font-semibold mb-4">Your Account</h2>
        <div className="space-y-4">
          <div>
            <p className="text-gray-600">Address</p>
            <p className="font-medium">{formatAddress(account)}</p>
          </div>
          <div>
            <p className="text-gray-600">YILDX Balance</p>
            <p className="font-medium">{formatTokens(tokenBalance)} YILDX</p>
          </div>
          <div>
            <p className="text-gray-600">Active Delegations</p>
            <p className="font-medium">{currentDelegate ? 1 : 0}</p>
          </div>
          {governanceInfo.votingPower > 0 && (
            <div>
              <p className="text-gray-600">Voting Power</p>
              <p className="font-medium">{governanceInfo.votingPower}%</p>
            </div>
          )}
          
          {/* Add a new section showing stats about delegated power */}
          {parseFloat(delegatedVotingPower) > 0 && (
            <div>
              <p className="text-gray-600">Votes Delegated To You</p>
              <p className="font-medium">{formatTokens(delegatedVotingPower)} votes</p>
              <p className="text-xs text-gray-500">
                From {userDelegators.length} address{userDelegators.length !== 1 ? 'es' : ''}
              </p>
            </div>
          )}
          
          {/* Add voting history summary */}
          <div>
            <p className="text-gray-600">Your Votes Cast</p>
            <p className="font-medium">{votingHistory.length}</p>
          </div>
        </div>
        
        {/* My Delegator Profile */}
        {isUserDelegator && (
          <div className="mt-6 border-t pt-4">
            <h3 className="text-lg font-medium mb-2">My Delegator Profile</h3>
            <div className="bg-green-50 p-4 rounded-lg shadow-sm border border-green-100">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold">{delegatorProfile.name}</p>
                  <p className="text-sm text-gray-600 mt-1">{delegatorProfile.description}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  delegatorProfile.acceptingDelegations 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {delegatorProfile.acceptingDelegations ? 'Accepting Delegations' : 'Not Accepting Delegations'}
                </span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={() => onEditProfile({
                    name: delegatorProfile.name,
                    description: delegatorProfile.description,
                    acceptingDelegations: delegatorProfile.acceptingDelegations
                  })}
                  className="bg-blue-600 hover:bg-blue-700 text-white py-1 px-3 rounded text-sm"
                >
                  Edit Profile
                </button>
                <button
                  onClick={onToggleAcceptingDelegations}
                  className={`${
                    delegatorProfile.acceptingDelegations 
                      ? 'bg-red-600 hover:bg-red-700' 
                      : 'bg-green-600 hover:bg-green-700'
                  } text-white py-1 px-3 rounded text-sm`}
                >
                  {delegatorProfile.acceptingDelegations ? 'Stop Accepting' : 'Start Accepting'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Operator Info (if there is one) */}
        {operatorInfo && (
          <div className="mt-6 border-t pt-4">
            <h3 className="text-lg font-medium mb-2">Network Operator</h3>
            <div className="bg-indigo-50 p-4 rounded-lg shadow-sm border border-indigo-100">
              <div className="flex items-center">
                <span className="inline-block bg-indigo-500 text-white text-xs font-semibold mr-2 px-2.5 py-0.5 rounded">
                  Operator
                </span>
                {operatorInfo.name && (
                  <p className="font-semibold">{operatorInfo.name}</p>
                )}
              </div>
              <p className="font-medium mt-1">{formatAddress(operatorInfo.address)}</p>
              {operatorInfo.description && (
                <p className="text-sm text-gray-600 mt-1">{operatorInfo.description}</p>
              )}
              <div className="flex justify-between mt-2 text-sm">
                <span>{formatTokens(operatorInfo.tokens)} YILDX</span>
              </div>
            </div>
          </div>
        )}
        
        {/* Current delegation info */}
        {currentDelegate && (
          <div className="mt-6 border-t pt-4">
            <h3 className="text-lg font-medium mb-2">Current Delegation</h3>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              {currentDelegate.name && (
                <p className="font-semibold">{currentDelegate.name}</p>
              )}
              <p className="font-medium">{formatAddress(currentDelegate.address)}</p>
              <p className="text-sm text-gray-600 mt-1">{currentDelegate.description}</p>
              <div className="flex justify-between mt-2 text-sm">
                <span>{formatTokens(currentDelegate.tokens)} YILDX</span>
                {currentDelegate.approvalRate !== null ? (
                  <span>{currentDelegate.approvalRate}% approval</span>
                ) : (
                  <span className="text-gray-500 italic">No voting history</span>
                )}
              </div>
              <button 
                onClick={onRemoveDelegation}
                className="mt-3 w-full bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded"
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Remove Delegation'}
              </button>
            </div>
          </div>
        )}
        
        {/* Delegators to Me Section */}
        <DelegatorsToMeSection 
          userDelegators={userDelegators} 
          delegatedVotingPower={delegatedVotingPower}
          formatAddress={formatAddress}
          formatTokens={formatTokens}
        />
        
        {/* Active proposals summary */}
        <div className="mt-6 border-t pt-4">
          <h3 className="text-lg font-medium mb-2">Governance Activity</h3>
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Active Proposals</span>
              <span className="font-medium">{activeProposals}</span>
            </div>
            <div className="mt-3">
              <a href="/governance" className="text-blue-600 hover:text-blue-800 text-sm">
                View Governance Portal →
              </a>
            </div>
          </div>
        </div>
        
        {/* Recent voting activity */}
        {votingHistory.length > 0 && (
          <div className="mt-6 border-t pt-4">
            <h3 className="text-lg font-medium mb-2">Recent Voting Activity</h3>
            <div className="bg-white p-4 rounded-lg shadow-sm overflow-hidden">
              <div className="space-y-3 max-h-56 overflow-y-auto">
                {votingHistory.slice(0, 5).map((vote, index) => (
                  <div key={index} className="border-b pb-2 last:border-b-0">
                    <p className="text-sm font-medium line-clamp-1">{vote.description}</p>
                    <div className="flex justify-between mt-1 text-xs">
                      <span className={`${vote.support ? 'text-green-600' : 'text-red-600'} font-medium`}>
                        {vote.support ? 'Voted For' : 'Voted Against'}
                      </span>
                      <span className="text-gray-500">{new Date(vote.timestamp * 1000).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
              {votingHistory.length > 5 && (
                <div className="mt-2 text-center">
                  <button className="text-blue-600 hover:text-blue-800 text-sm">
                    View All Votes ({votingHistory.length})
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Component for showing accounts delegating to current user
export const DelegatorsToMeSection = ({ userDelegators, delegatedVotingPower, formatAddress, formatTokens }) => {
  if (userDelegators.length === 0) return null;
  
  return (
    <div className="mt-6 border-t pt-4">
      <h3 className="text-lg font-medium mb-2">Accounts Delegating To You</h3>
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <p className="text-sm text-gray-600 mb-2">
          The following accounts have delegated their voting power to you:
        </p>
        <div className="max-h-60 overflow-y-auto">
          {userDelegators.map((delegator, index) => (
            <div key={index} className="border-b last:border-b-0 py-2">
              <p className="font-medium">{formatAddress(delegator)}</p>
            </div>
          ))}
        </div>
        <div className="mt-3 text-sm">
          <span className="font-medium text-green-600">Total delegated voting power: </span>
          {formatTokens(delegatedVotingPower)} voting power
        </div>
      </div>
    </div>
  );
};

// Component for delegators list
export const DelegatorsList = ({
  delegators,
  currentDelegate,
  delegatorProfile,
  loading,
  account,
  onDelegate,
  onEditProfile,
  onToggleAcceptingDelegations,
  onRemoveDelegation,
  formatAddress,
  formatTokens
}) => {
  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (delegators.length === 0) {
    return <p>No delegators found matching your search.</p>;
  }
  
  return (
    <div className="space-y-4">
      {delegators.map((delegator, index) => (
        <div 
          key={index} 
          className={`border rounded-lg p-4 hover:bg-gray-50 ${
            delegator.isOperator ? 'border-indigo-200 bg-indigo-50' : 
            delegator.isCurrentUser ? 'border-green-200 bg-green-50' : ''
          }`}
        >
          <div className="flex justify-between items-start">
            <div>
              {delegator.name && (
                <p className="font-semibold">{delegator.name}</p>
              )}
              <p className="font-medium">{formatAddress(delegator.address)}</p>
              <p className="text-sm text-gray-600 mt-1">{delegator.description}</p>
              
              <div className="flex flex-wrap gap-2 mt-1">
                {delegator.isOperator && (
                  <span className="inline-block bg-indigo-500 text-white text-xs font-semibold px-2.5 py-0.5 rounded">
                    Network Operator
                  </span>
                )}
                {delegator.isCurrentUser && (
                  <span className="inline-block bg-green-500 text-white text-xs font-semibold px-2.5 py-0.5 rounded">
                    You
                  </span>
                )}
                
                {/* Show delegation acceptance status for all */}
                <span className={`inline-block text-xs px-2.5 py-0.5 rounded ${
                  delegator.acceptingDelegations 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {delegator.acceptingDelegations 
                    ? 'Accepting Delegations' 
                    : 'Not Accepting Delegations'}
                </span>
                
                {/* Show delegations count if any */}
                {delegator.delegatorsCount > 0 && (
                  <span className="inline-block bg-purple-100 text-purple-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                    {delegator.delegatorsCount} Delegator{delegator.delegatorsCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
            
            {delegator.isCurrentUser ? (
              // User's own delegator card
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => onEditProfile(delegator)}
                  className="bg-blue-600 hover:bg-blue-700 text-white py-1 px-3 rounded text-sm"
                >
                  Edit Profile
                </button>
                <button
                  onClick={onToggleAcceptingDelegations}
                  className={`${
                    delegatorProfile.acceptingDelegations 
                      ? 'bg-red-600 hover:bg-red-700' 
                      : 'bg-green-600 hover:bg-green-700'
                  } text-white py-1 px-3 rounded text-sm`}
                >
                  {delegatorProfile.acceptingDelegations ? 'Stop Accepting' : 'Start Accepting'}
                </button>
              </div>
            ) : (
              // Other delegator card
              delegator.acceptingDelegations ? (
                <button 
                  onClick={currentDelegate && currentDelegate.address === delegator.address ? 
                    onRemoveDelegation : 
                    () => onDelegate(delegator)
                  }
                  className={`${
                    delegator.isOperator ? 'bg-indigo-600 hover:bg-indigo-700' : 
                    currentDelegate && currentDelegate.address === delegator.address ? 
                    'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
                  } text-white py-1 px-3 rounded text-sm`}
                  disabled={loading}
                >
                  {currentDelegate && currentDelegate.address === delegator.address 
                    ? 'Remove Delegation' 
                    : 'Delegate'}
                </button>
              ) : (
                <span className="text-xs bg-gray-200 text-gray-700 py-1 px-3 rounded-full">
                  Not accepting delegations
                </span>
              )
            )}
          </div>
          <div className="mt-3 grid grid-cols-4 gap-2 text-sm">
            <div>
              <p className="text-gray-500">Holdings</p>
              <p className="font-medium">{formatTokens(delegator.tokens)} YILDX</p>
            </div>
            <div>
              <p className="text-gray-500">Votes Cast</p>
              <p className="font-medium">{delegator.votingHistory}</p>
            </div>
            <div>
              <p className="text-gray-500">Approval Rate</p>
              {delegator.approvalRate !== null ? (
                <p className="font-medium">{delegator.approvalRate}%</p>
              ) : (
                <p className="text-gray-500 italic">None</p>
              )}
            </div>
            <div>
              <p className="text-gray-500">Delegated To</p>
              <p className="font-medium">{formatTokens(delegator.delegatedPower)}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Component for delegation statistics section
export const DelegationStatsSection = ({
  showDelegationStats,
  delegationStatistics,
  loading,
  operatorAddress,
  account,
  onToggleStats,
  formatAddress,
  formatTokens
}) => {
  if (!showDelegationStats) {
    return (
      <div className="mt-8">
        <button
          onClick={onToggleStats}
          className="text-blue-600 hover:text-blue-800 font-medium"
        >
          Show Delegation Statistics
        </button>
      </div>
    );
  }
  
  return (
    <div className="mt-8 border-t pt-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Network Delegation Statistics</h3>
        <button
          onClick={onToggleStats}
          className="text-gray-600 hover:text-gray-800 text-sm"
        >
          Hide Statistics
        </button>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : delegationStatistics.totalDelegations > 0 ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 mb-4">
            There are currently {delegationStatistics.totalDelegations} active delegations from {delegationStatistics.totalDelegators} delegators 
            to {delegationStatistics.totalDelegatees} delegatees.
          </p>
          
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-2 px-4 border-b text-left">Delegatee</th>
                  <th className="py-2 px-4 border-b text-right">Delegators</th>
                  <th className="py-2 px-4 border-b text-right">Total Delegated</th>
                  <th className="py-2 px-4 border-b text-right">Votes Cast</th>
                </tr>
              </thead>
              <tbody>
                {delegationStatistics.delegationsByDelegatee.map((stat, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="py-2 px-4 border-b">
                      <div>
                        {stat.name && <p className="font-medium">{stat.name}</p>}
                        <p className="text-gray-600">{formatAddress(stat.address)}</p>
                        {stat.address.toLowerCase() === operatorAddress.toLowerCase() && (
                          <span className="inline-block bg-indigo-500 text-white text-xs font-semibold mt-1 px-2 py-0.5 rounded">
                            Operator
                          </span>
                        )}
                        {stat.address.toLowerCase() === account?.toLowerCase() && (
                          <span className="inline-block bg-green-500 text-white text-xs font-semibold mt-1 px-2 py-0.5 rounded ml-1">
                            You
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-4 border-b text-right">{stat.delegatorCount}</td>
                    <td className="py-2 px-4 border-b text-right">
                      {formatTokens(stat.totalPower)} YILDX
                    </td>
                    <td className="py-2 px-4 border-b text-right">{stat.votingHistoryCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <p className="text-gray-600">No delegations found in the network.</p>
      )}
    </div>
  );
};

// Component for delegator profile form
export const DelegatorProfileForm = ({
  delegatorProfile,
  loading,
  onProfileChange,
  onSubmit,
  isEditing,
  onCancel
}) => {
  return (
    <div className={`mt-4 ${isEditing ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'} p-4 rounded-lg border`}>
      <h4 className="font-medium mb-3">{isEditing ? 'Edit Delegator Profile' : 'Delegator Profile'}</h4>
      
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Display Name
          </label>
          <input
            type="text"
            name="name"
            value={delegatorProfile.name}
            onChange={onProfileChange}
            className="w-full p-2 border border-gray-300 rounded"
            placeholder="Your name or pseudonym"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            name="description"
            value={delegatorProfile.description}
            onChange={onProfileChange}
            className="w-full p-2 border border-gray-300 rounded"
            placeholder="Describe your investment philosophy and governance approach"
            rows={3}
          />
        </div>
        
        <div className="flex items-center">
          <input
            type="checkbox"
            name="acceptingDelegations"
            checked={delegatorProfile.acceptingDelegations}
            onChange={onProfileChange}
            className="mr-2"
            id={isEditing ? "editAcceptDelegations" : "acceptDelegations"}
          />
          <label htmlFor={isEditing ? "editAcceptDelegations" : "acceptDelegations"} className="text-sm text-gray-700">
            I am accepting delegations from other token holders
          </label>
        </div>
        
        {isEditing ? (
          <div className="pt-3 flex gap-2">
            <button
              onClick={onSubmit}
              className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded flex-1"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              onClick={onCancel}
              className="bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded flex-1"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="pt-3">
            <button
              onClick={onSubmit}
              className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded w-full"
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Submit Profile'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Component for delegation confirmation modal
export const DelegationConfirmationModal = ({
  showConfirmation,
  selectedDelegator,
  loading,
  onConfirm,
  onCancel,
  formatAddress,
  formatTokens
}) => {
  if (!showConfirmation || !selectedDelegator) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-xl font-bold mb-4">Confirm Delegation</h3>
        <p className="mb-4">
          You are about to delegate your voting power to:
        </p>
        <div className="bg-gray-50 p-4 rounded-lg mb-4">
          {selectedDelegator.name && (
            <p className="font-semibold">{selectedDelegator.name}</p>
          )}
          <p className="font-medium">{formatAddress(selectedDelegator.address)}</p>
          <p className="text-sm text-gray-600 mt-1">{selectedDelegator.description}</p>
          <div className="flex justify-between mt-2 text-sm">
            <span>{formatTokens(selectedDelegator.tokens)} YILDX</span>
            {selectedDelegator.approvalRate !== null ? (
              <span>{selectedDelegator.approvalRate}% approval</span>
            ) : (
              <span className="text-gray-500 italic">No voting history</span>
            )}
          </div>
          
          {selectedDelegator.isOperator && (
            <div className="mt-2">
              <span className="inline-block bg-indigo-500 text-white text-xs font-semibold px-2.5 py-0.5 rounded">
                Network Operator
              </span>
            </div>
          )}
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Note: You maintain full ownership of your tokens. You can remove this delegation at any time.
        </p>
        <p className="text-sm text-blue-600 mb-4">
          This action will create a blockchain transaction. Please confirm with your wallet after clicking "Confirm Delegation".
        </p>
        <div className="flex space-x-3">
          <button 
            onClick={onCancel}
            className="flex-1 bg-gray-200 hover:bg-gray-300 py-2 px-4 rounded"
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            onClick={onConfirm}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Confirm Delegation'}
          </button>
        </div>
      </div>
    </div>
  );
};